import { useMemo, useState } from "react";
import { useQuery, type ApolloError } from "@apollo/client";
import { useParams, useSearchParams } from "react-router-dom";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { SectionDetailsHeader } from "../_components/SectionDetailsHeader";
import { SectionPeopleTabs } from "../_components/SectionPeopleTabs";
import usePageTitle from "@/hooks/usePageTitle";
import Chip, { ChipSet } from "@/components/Chip";
import DetailsCard from "@/components/DetailsCard";
import MultiSelect from "@/components/MultiSelect";
import EmptyState from "@/components/EmptyState";
import SubSectionHeading from "@/components/SubSectionHeading";
import { TableHeading } from "@/components/Table";
import DateRangeScroller, {
  parsePeriodFromSearch,
  periodToSearch,
  type PeriodWindow
} from "@/components/DateRangeScroller";
import EventGroups from "./_components/EventGroups";
import PeriodStats from "./_components/PeriodStats";
import CopyActions from "./_components/CopyActions";
import RosterActivityPageSkeleton from "./_components/RosterActivityPageSkeleton";
import { SECTION_ROSTER_ACTIVITY_QUERY } from "@/features/roster-activity";
import {
  ALL_KINDS,
  COHORT_LABELS,
  DEFAULT_COHORTS,
  KIND_LABELS,
  summarizeSelectedKinds,
  type RosterActivityCohort,
  type RosterActivityEvent,
  type RosterActivityKind
} from "@/features/roster-activity";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { sendToRaygun } from "@/lib/raygunUtils";

function countByKind(events: RosterActivityEvent[]): Record<RosterActivityKind, number> {
  const counts = Object.fromEntries(ALL_KINDS.map((kind) => [kind, 0])) as Record<RosterActivityKind, number>;
  for (const event of events) {
    counts[event.kind] += 1;
  }
  return counts;
}

function countByCohort(events: RosterActivityEvent[]): Record<RosterActivityCohort, number> {
  const counts = Object.fromEntries(DEFAULT_COHORTS.map((cohort) => [cohort, 0])) as Record<
    RosterActivityCohort,
    number
  >;
  for (const event of events) {
    counts[event.cohort] += 1;
  }
  return counts;
}

export default function RosterActivityPage() {
  useRequireCapability({ domain: DOMAIN.SECTIONS, permission: PERMISSION.viewSection });
  const { sectionId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const period = useMemo(() => parsePeriodFromSearch(searchParams), [searchParams]);
  const [kinds, setKinds] = useState<RosterActivityKind[]>(ALL_KINDS);
  const [cohorts, setCohorts] = useState<RosterActivityCohort[]>(DEFAULT_COHORTS);
  const { setError, clearError } = useErrorBanner();

  const startDateTime = period.start.toUTC().toISO() ?? "";
  const endDateTime = period.end.toUTC().toISO() ?? "";
  const filtersEmpty = kinds.length === 0 || cohorts.length === 0;

  const {
    data,
    previousData,
    loading,
    error: eventsError,
    refetch
  } = useQuery(SECTION_ROSTER_ACTIVITY_QUERY, {
    // Load the full period window; kind/cohort chips filter client-side so counts stay available.
    variables: { sectionId, startDateTime, endDateTime },
    skip: !sectionId || !startDateTime || !endDateTime,
    notifyOnNetworkStatusChange: true,
    onCompleted: () => clearError(),
    onError: (error: ApolloError) => {
      sendToRaygun(error, { tags: ["section-roster-activity", "events"] });
      setError(error.message);
    }
  });

  const resolvedData = data ?? previousData;
  const allEvents = useMemo(() => resolvedData?.section?.rosterActivity ?? [], [resolvedData]);
  const events = useMemo(() => {
    if (filtersEmpty) return [];
    return allEvents.filter((event) => kinds.includes(event.kind) && cohorts.includes(event.cohort));
  }, [allEvents, filtersEmpty, kinds, cohorts]);

  const kindCounts = useMemo(
    () => countByKind(allEvents.filter((event) => cohorts.includes(event.cohort))),
    [allEvents, cohorts]
  );
  const cohortCounts = useMemo(
    () => countByCohort(allEvents.filter((event) => kinds.includes(event.kind))),
    [allEvents, kinds]
  );

  const kindOptions = useMemo(
    () =>
      ALL_KINDS.map((kind) => ({
        value: kind,
        label: `${KIND_LABELS[kind]} (${kindCounts[kind]})`
      })),
    [kindCounts]
  );

  const kindsSummary = useMemo(() => summarizeSelectedKinds(kinds, kindCounts), [kinds, kindCounts]);

  const sectionName = resolvedData?.section?.name ?? "Section";
  usePageTitle("/sections/:sectionId/roster-activity", `Sections | Section Details | Roster Activity | ${sectionName}`);

  // Full-page loading only on first load when there is no data yet.
  const eventsBusy = loading && !resolvedData;
  const refetching = Boolean(loading && resolvedData);
  // Hide copy while events refetch so the new period.label cannot stamp onto previousData rows.
  const copyActionsHidden = Boolean(eventsError) || loading || filtersEmpty;

  const updatePeriod = (next: PeriodWindow) => {
    setSearchParams(periodToSearch(next), { replace: true });
  };

  const clearKinds = () => setKinds([]);
  const selectAllKinds = () => setKinds([...ALL_KINDS]);

  const toggleCohort = (cohort: RosterActivityCohort) => {
    setCohorts((current) => {
      if (current.includes(cohort)) {
        if (current.length === 1) return current;
        return current.filter((value) => value !== cohort);
      }
      return [...current, cohort];
    });
  };

  const changeTypeFilter = (
    <MultiSelect
      label="Change Type"
      options={kindOptions}
      selectedValues={kinds}
      selectedText={kinds.length === 0 ? "None selected" : kindsSummary}
      selectAllLabel="Select All"
      selectNoneLabel="Select None"
      closeOnSelectAll
      onSelect={(value, checked) => {
        if (!(ALL_KINDS as readonly string[]).includes(value)) return;
        const kind = value as RosterActivityKind;
        setKinds((current) => {
          if (checked) {
            return current.includes(kind) ? current : [...current, kind];
          }
          return current.filter((item) => item !== kind);
        });
      }}
      onClear={selectAllKinds}
      onSelectNone={clearKinds}
      className="w-[18rem] max-w-full"
    />
  );

  return (
    <div data-testid="roster-activity-page">
      <SectionDetailsHeader sectionId={sectionId} />
      <SectionPeopleTabs />

      <TableHeading
        heading="Roster Activity"
        body="Review who joined, changed jobs, retired, or passed away in this section."
        actions={
          copyActionsHidden
            ? undefined
            : [
                {
                  label: "Copy Filtered Results",
                  icon: "content_copy",
                  variant: "outlined",
                  onClick: () => {},
                  render: () => <CopyActions events={events} />
                }
              ]
        }
      />

      {eventsBusy ? (
        <RosterActivityPageSkeleton includeFilters />
      ) : (
        <>
          <DetailsCard>
            <div className="flex w-full flex-col gap-6">
              <div className="flex w-full flex-col items-start gap-4">
                <SubSectionHeading>Filter By Date Range</SubSectionHeading>
                <DateRangeScroller value={period} onChange={updatePeriod} />
              </div>
              <div className="flex w-full flex-col items-start gap-4">
                <SubSectionHeading>Filter By Type</SubSectionHeading>
                <div className="flex w-full flex-wrap items-center gap-4">
                  <div className="shrink-0">{changeTypeFilter}</div>
                  <div role="group" aria-label="Member Type" className="min-w-0">
                    <ChipSet>
                      {DEFAULT_COHORTS.map((cohort) => {
                        const selected = cohorts.includes(cohort);
                        const locked = selected && cohorts.length === 1;
                        return (
                          <Chip
                            key={cohort}
                            label={`${COHORT_LABELS[cohort]} (${cohortCounts[cohort]})`}
                            selected={selected}
                            softDisabled={locked}
                            onClick={() => toggleCohort(cohort)}
                          />
                        );
                      })}
                    </ChipSet>
                  </div>
                </div>
              </div>
            </div>
          </DetailsCard>

          {eventsError ? (
            <EmptyState
              iconName="error"
              title="Unable to Load Roster Activity"
              caption="Try refreshing the page. If the problem continues, contact support."
              action={{ children: "Try Again", onClick: () => refetch() }}
            />
          ) : filtersEmpty ? (
            <EventGroups events={events} />
          ) : refetching ? (
            <RosterActivityPageSkeleton />
          ) : (
            <>
              <PeriodStats events={events} />
              <EventGroups events={events} />
            </>
          )}
        </>
      )}
    </div>
  );
}
